import { requireEnv } from '@/lib/env'
import { createClient } from '@nhost/nhost-js'
import { SESSION_COOKIE } from './session-cookie'
import { CookieSessionStorage } from './nhost-cookie-storage'

/**
 * Nhost client (self-hosted), browser side. `createClient` always wires the official client-side
 * middleware chain — auto-refresh near expiry, token attachment, session persistence — so there is
 * no separate "autoRefreshToken" flag to set (that option does not exist; the old code passing it
 * was a silent no-op).
 *
 * Session lives in a COOKIE so the Next.js server — the edge proxy (proxy.ts) and RSC/route handlers
 * via lib/nhost-server.ts — can read the SAME session and gate/render server-side without an auth
 * flash. We use a custom CookieSessionStorage instead of the SDK's built-in CookieStorage: the
 * built-in stores the bulky, derivable `decodedToken` in the cookie too, which for multi-property
 * admins pushes it past the 4 KB browser limit (the browser then drops it and the session never
 * persists). Ours stores the session without that copy and rebuilds it on read. See
 * lib/session-cookie-codec.ts. The cookie is NOT httpOnly (the browser SDK reads it from JS); Hasura
 * RLS is the real boundary.
 *
 * Core URLs fail loud if unset (no silent prod fallback) — a missing env is a deploy bug to surface.
 */
const authUrl = process.env.NEXT_PUBLIC_NHOST_AUTH_URL
const graphqlUrl = process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL
if (!authUrl) throw new Error('NEXT_PUBLIC_NHOST_AUTH_URL is not set')
if (!graphqlUrl) throw new Error('NEXT_PUBLIC_NHOST_GRAPHQL_URL is not set')

export { SESSION_COOKIE }

export const nhost = createClient({
  authUrl,
  graphqlUrl,
  storageUrl: process.env.NEXT_PUBLIC_NHOST_STORAGE_URL,
  functionsUrl: process.env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
  storage: new CookieSessionStorage(),
})

// Export individual services for convenience
export const { auth, graphql, storage, functions } = nhost

/**
 * Resolve a stored value (Nhost file id, raw UUID, or external URL) into a URL
 * suitable for `<img src>` / `<a href>`. Returns null for empty input.
 *
 * Mirrors the resolution logic inside FileUploadZone so anywhere a value
 * previously held by that component needs to render (menu item cards,
 * gallery thumbs, document previews) doesn't duplicate the id-vs-url branching.
 */
export function getNhostFileUrl(value?: string | null): string | null {
  if (!value) return null
  if (value.startsWith('http://') || value.startsWith('https://')) return value
  const baseUrl = (
    requireEnv(process.env.NEXT_PUBLIC_NHOST_STORAGE_URL, 'NEXT_PUBLIC_NHOST_STORAGE_URL')
  ).replace(/\/+$/, '')
  return `${baseUrl}/files/${value}`
}
